import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Slider, Switch } from 'antd';
import { Icon } from 'Components/UI/Icon/Icon';
import { BimSectionPlane } from 'Data/interfaces/BimViewer/IBimViewerPort';
import {
  SectionPanelWrapper,
  SectionHeader,
  SectionRow,
  AxisGroup,
  AxisButton,
  CloseButton,
} from './styles';

interface IBimSectionPanelProps {
  section: BimSectionPlane;
  onChange(next: Partial<BimSectionPlane>): void;
  onClose(): void;
}

// Os eixos do motor são nomeados pela direção do olhar do corte, não por X/Y/Z:
// `down` corta na horizontal (planta), `front` e `side` na vertical.
const AXES: { axis: BimSectionPlane['axis']; labelKey: string }[] = [
  { axis: 'down', labelKey: 'bim3dSection.axisHorizontal' },
  { axis: 'front', labelKey: 'bim3dSection.axisFront' },
  { axis: 'side', labelKey: 'bim3dSection.axisSide' },
];

/**
 * Criar/editar/remover um plano de corte. Cada mudança vai direto pro motor, que
 * aplica no frame seguinte — arrastar o slider corta em tempo real, sem
 * recarregar nem re-tesselar o modelo.
 */
export const BimSectionPanel: FC<IBimSectionPanelProps> = ({ section, onChange, onClose }) => {
  const { t } = useTranslation('viewer');

  return (
    <SectionPanelWrapper>
      <SectionHeader>
        <span className="sectionTitle">{t('bim3dSection.title')}</span>
        <Switch
          size="small"
          checked={section.enabled}
          onChange={(enabled) => onChange({ enabled })}
        />
        <CloseButton type="button" aria-label={t('bim3dSection.close')} onClick={onClose}>
          <Icon icon="cancelar" customSize={10} customColor="#8697a8" />
        </CloseButton>
      </SectionHeader>

      <AxisGroup>
        {AXES.map(({ axis, labelKey }) => (
          <AxisButton
            key={axis}
            type="button"
            $active={section.axis === axis}
            // Trocar de eixo com o corte desligado não mostraria nada; ligar
            // junto é o que o usuário quer em 100% dos casos.
            onClick={() => onChange({ axis, enabled: true })}
          >
            {t(labelKey)}
          </AxisButton>
        ))}
      </AxisGroup>

      <SectionRow>
        <span className="sectionLabel">{t('bim3dSection.position')}</span>
        <Slider
          className="sectionSlider"
          min={0}
          max={100}
          value={section.position}
          disabled={!section.enabled}
          tooltip={{ open: false }}
          onChange={(position: number) => onChange({ position })}
        />
        <span className="sectionValue">{section.position}%</span>
      </SectionRow>

      <SectionRow>
        <span className="sectionLabel">{t('bim3dSection.flip')}</span>
        <Switch
          size="small"
          checked={!!section.flipped}
          disabled={!section.enabled}
          onChange={(flipped) => onChange({ flipped })}
        />
      </SectionRow>
    </SectionPanelWrapper>
  );
};
